import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import { getGreenCellColor, getRedGreenCell, getRedGreenCellno0, getRedCellColor } from './base/colorUtils';
import { customDarkSelectStyles } from './selects/CustomDarkSelectStyles';
import axiosInstance from './backend/axiosInstance';
import LeagueCustomSelect from './selects/LeagueCustomSelect';
import TeamCustomSelect from './selects/TeamCustomSelect';
import PatchCustomSelect from './selects/PatchCustomSelect';
import { backgroundColor } from './base/colorUtils';
import { columnNames, columnNamesDescription } from './base/stats';
import { useVirtualizer } from '@tanstack/react-virtual'
import { motion, AnimatePresence } from "framer-motion";
import { PlayerPhoto } from './data/PlayerPhoto';
import Select from "react-select";
import "./Players.css";
import {
    ScatterChart,
    Scatter,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer
} from "recharts";

const getPlayerNameWithoutTeamTag = (fullName) => {
    const parts = fullName.split(' ');
    return parts.slice(1).join(' ').toLowerCase();
};


const colorOptions = [
    { label: "Random", value: "Random" },
    { label: "By Team", value: "teamName" },
];

const nameDisplayOptions = [
    { label: "None", value: "none" },
    { label: "Selected Only", value: "selected" },
    { label: "All", value: "all" },
];

const getRandomColor = () =>
    `hsl(${Math.floor(Math.random() * 360)}, 70%, 50%)`;

const PlayersScatterPlot = ({
    data,
    selectedPlayers,
    columnOptions
}) => {
    const [colorMode, setColorMode] = useState("Random");
    const [nameMode, setNameMode] = useState("selected");

    const selectedSet = useMemo(
        () => new Set(selectedPlayers),
        [selectedPlayers]
    );

    // 🔥 Auto detect numeric keys
    const numericOptions = useMemo(() => {
        if (!columnOptions || !data?.length) return [];

        const sample = data[0];

        return Object.keys(columnOptions)
            .filter((key) => columnOptions[key]) // only enabled columns
            .filter((key) => typeof sample[key] === "number") // only numeric
            .map((key) => ({
                label: columnNames?.[key] ?? key,
                value: key,
            }));
    }, [columnOptions, data]);

    const [xKey, setXKey] = useState(null);
    const [yKey, setYKey] = useState(null);

    // Set defaults once numeric options exist
    React.useEffect(() => {
        if (numericOptions.length >= 2 && !xKey && !yKey) {
            const shuffled = [...numericOptions].sort(() => 0.5 - Math.random());

            setXKey(shuffled[0]);
            setYKey(shuffled[1]);
        }
    }, [numericOptions, xKey, yKey]);

    // 🎨 Color logic
    const coloredData = useMemo(() => {
        const groupColors = {};

        return data.map((player) => {
            let groupKey =
                colorMode === "Random"
                    ? player.displayName
                    : player[colorMode] || "Unknown";

            if (!groupColors[groupKey]) {
                groupColors[groupKey] = getRandomColor();
            }

            return {
                ...player,
                color: groupColors[groupKey],
                isSelected: selectedSet.has(player.displayName),
            };
        });
    }, [data, colorMode, selectedSet]);

    const CustomDot = (props) => {
        const { cx, cy, payload } = props;
        if (!payload) return null;

        return (
            <g>
                <circle
                    cx={cx}
                    cy={cy}
                    r={payload.isSelected ? 9 : 5}
                    fill={payload.color}
                    stroke={payload.isSelected ? "#ffffff" : "none"}
                    strokeWidth={payload.isSelected ? 2 : 0}
                />
                {(nameMode === "all" ||
                    (nameMode === "selected" && payload.isSelected)) && (
                        <text
                            x={cx + 8}
                            y={cy}
                            fontSize={11}
                            fill="white"
                        >
                            {payload.displayName}
                        </text>
                    )}
            </g>
        );
    };

    const CustomTooltip = ({ active, payload }) => {
        if (!active || !payload || !payload.length) return null;

        const player = payload[0].payload;
        const nameWithoutTag = getPlayerNameWithoutTeamTag(player.displayName) || player.displayName;
        const avatarUrl = PlayerPhoto?.[nameWithoutTag] || PlayerPhoto?.[1];
        return (
            <div
                style={{
                    background: "#111",
                    border: "1px solid #333",
                    borderRadius: 12,
                    padding: 12,
                    color: "#fff",
                    minWidth: 220,
                    boxShadow: "0 8px 24px rgba(0,0,0,0.6)",
                }}
            >
                {/* Header */}
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    {avatarUrl && (
                        <img
                            src={avatarUrl}
                            alt={player.displayName}
                            style={{
                                width: 40,
                                height: 40,
                                borderRadius: "50%",
                                objectFit: "cover",
                                border: `2px solid ${player.color}`,
                            }}
                        />
                    )}

                    <div>
                        <div style={{ fontWeight: 600, fontSize: 14 }}>
                            {player.displayName}
                        </div>
                    </div>
                </div>

                {/* Divider */}
                <div
                    style={{
                        height: 1,
                        background: "#333",
                        margin: "10px 0",
                    }}
                />

                {/* Stats */}
                <div style={{ fontSize: 13, lineHeight: 1.6 }}>
                    <div>
                        <strong>{xKey.label}:</strong>{" "}
                        {player[xKey.value].toFixed(2)}
                    </div>
                    <div>
                        <strong>{yKey.label}:</strong>{" "}
                        {player[yKey.value].toFixed(2)}
                    </div>
                </div>
            </div>
        );
    };
    if (!xKey || !yKey) return null;


    return (
        <div style={{ width: "100%", height: 550, marginBottom: 100, padding: 15 }}>

            {/* Controls */}
            <div
                style={{
                    display: "flex",
                    gap: 15,
                    marginTop: 30,
                    marginBottom: 15,
                    justifyContent: "center",
                    flexWrap: "wrap",
                }}
            >
                <div style={{ minWidth: 200 }}>
                    <Select
                        styles={customDarkSelectStyles}
                        options={numericOptions}
                        value={xKey}
                        onChange={setXKey}
                        placeholder="Select X Axis"
                    />
                </div>
                <div style={{ minWidth: 200 }}>
                    <Select
                        styles={customDarkSelectStyles}
                        options={numericOptions}
                        value={yKey}
                        onChange={setYKey}
                        placeholder="Select Y Axis"
                    />
                </div>

                <div style={{ minWidth: 200 }}>
                    <Select
                        styles={customDarkSelectStyles}
                        options={colorOptions}
                        value={colorOptions.find(o => o.value === colorMode)}
                        onChange={(opt) => setColorMode(opt.value)}
                    />
                </div>

                <div style={{ minWidth: 200 }}>
                    <Select
                        styles={customDarkSelectStyles}
                        options={nameDisplayOptions}
                        value={nameDisplayOptions.find(o => o.value === nameMode)}
                        onChange={(opt) => setNameMode(opt.value)}
                    />
                </div>
            </div>

            {/* Chart */}
            <ResponsiveContainer>
                <ScatterChart style={{ backgroundColor: "black" }}>
                    <CartesianGrid strokeDasharray="3 3" />

                    <XAxis
                        type="number"
                        dataKey={xKey.value}
                        domain={['auto', 'auto']}
                        tick={{ fill: "#aaa" }}
                        label={{
                            value: xKey.label,
                            position: "bottom",
                            fill: "#fff",
                            fontSize: 14,
                            dy: -25,
                        }}
                    />

                    <YAxis
                        type="number"
                        dataKey={yKey.value}
                        domain={['auto', 'auto']}
                        tick={{ fill: "#aaa" }}
                        label={{
                            value: yKey.label,
                            angle: -90,
                            position: "insideLeft",
                            fill: "#fff",
                            fontSize: 14,
                            dy: -10,
                        }}
                    />
                    <Tooltip
                        content={<CustomTooltip />}
                        cursor={{ stroke: "#666", strokeDasharray: "3 3" }}
                    />
                    <Scatter
                        data={coloredData}
                        shape={<CustomDot />}
                    />
                </ScatterChart>
            </ResponsiveContainer>
        </div>
    );
};



const Players2 = () => {
    document.title = "Players";

    const [selectedLeague, setSelectedLeague] = useState([]);
    const [filterLeague, setFilterLeague] = useState([]);
    const [leagueOption, setLeagueOption] = useState([]);

    const [selectedPatch, setSelectedPatch] = useState([]);
    const [filterPatch, setFilterPatch] = useState([]);
    const [patchOption, setPatchOption] = useState([]);

    const [selectedTeam, setSelectedTeam] = useState([]);
    const [filterTeam, setFilterTeam] = useState([]);
    const [teamOptions, setTeamOptions] = useState([]);

    const [ShowSelected, setShowSelected] = useState(false);
    const [playerData, setPlayerData] = useState([]);
    const [sortedColumn, setSortedColumn] = useState('displayName');
    const [sortDirection, setSortDirection] = useState('asc');

    const [selectedPlayers, setSelectedPlayers] = useState([]);
    const [isOpen, setIsOpen] = useState(false);
    const toggleDropdown = () => setIsOpen(!isOpen);


    const username = localStorage.getItem("user");
    const tableContainerRef = useRef(null);
    const scrollTopRef = useRef(0);
    const scrollLeftRef = useRef(0);

    const [visibleColumns, setVisibleColumns] = useState({
        displayName: true,
        matches: true,
        rounds: true,
        kills: true,
        deaths: true,
        assists: true,
        ACS: true,
        KD: true,
        ADR: true,
        KPR: true,
        APR: true,
        FKPR: true,
        FDPR: true,
        HS: true,
        survival: true,
    });

    const handlePlayerClick = (teamName) => {
        window.location.href = `/player/${teamName}`;
    };

    useEffect(() => {
        axiosInstance.get(`userleague/?name=${username}`)
            .then((response) => {
                const userLeagues = response.data.map((item) => item.LeagueName);

                const options = userLeagues
                    .map(name => ({ value: name, label: name }))
                    .sort((a, b) => a.value.localeCompare(b.value));

                setLeagueOption(options);
            })
            .catch((error) => {
                console.error("Error fetching user leagues:", error);
            });
    }, [username]);


    useEffect(() => {
        axiosInstance
            .get('players/')
            .then(response => {
                setPlayerData(response.data || []);

                if (patchOption.length === 0) {
                    setPatchOption(response.data.filters.patches.map(p => ({ value: p, label: p })));
                }
                setTeamOptions(response.data.filters.teams.map(t => ({ value: t, label: t })));
            })
            .catch(error => console.error("Error fetching players:", error));
    }, [filterPatch, filterLeague, leagueOption]);




    const handleSort = useCallback((columnName) => {
        if (tableContainerRef.current) {
            scrollTopRef.current = tableContainerRef.current.scrollTop;
            scrollLeftRef.current = tableContainerRef.current.scrollLeft;
        }

        if (sortedColumn === columnName) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSortedColumn(columnName);
            setSortDirection('asc');
        }
    }, [sortedColumn, sortDirection]);



    const compareValues = (key, order = 'asc') => {
        return function (a, b) {
            if (!a.hasOwnProperty(key) || !b.hasOwnProperty(key)) {
                return 0;
            }

            const varA = typeof a[key] === 'string' ? a[key].toUpperCase() : a[key];
            const varB = typeof b[key] === 'string' ? b[key].toUpperCase() : b[key];

            let comparison = 0;
            if (varA > varB) {
                comparison = 1;
            } else if (varA < varB) {
                comparison = -1;
            }
            return order === 'desc' ? comparison * -1 : comparison;
        };
    };

    function getColorForCell(key, value, player) {
        switch (key) {
            case 'matches':
            case 'rounds':
            case 'displayName':
                return "rgb(30, 30, 30)"
            case 'deaths':
                return getRedCellColor(value, getMaxValue(key));
            case 'KD':
            case 'ACS':
            case 'ADR':
            case 'KPR':
            case 'APR':
            case 'FKPR':
            case 'FDPR':
            case 'HS':
            case 'survival':
                return getRedGreenCellno0(value, getMinValue(key), getMaxValue(key));
            case 'kills':
            case 'assists':
                return getGreenCellColor(value, getMaxValue(key));

            default:
                return "black"
        }
    }

    function formatValue(value, key) {
        switch (key) {
            case 'displayName':
                return (
                    <div style={{ textDecoration: 'none', color: 'inherit', textAlign: 'left' }}>
                        {value}
                    </div>
                );
            case "ACS":
            case "ADR":
                return `${value.toFixed(0)}`;

            case 'FDPR':
            case "FKPR":
            case "APR":
            case "KD":
            case "KPR":

                return `${value.toFixed(2)}`;
            case "level6Time":
            case "questTime":
                return formatSeconds(value);

            case "HS":
            case "survival":
                return `${value.toFixed(0)}%`;

            default:
                return value;
        }
    }

    const filteredPlayerData = useMemo(() =>
        playerData.filter(p =>
            (filterTeam.length === 0 || filterTeam.includes(p.teamName))
        ),
        [playerData, filterTeam]
    );

    const statsRanges = useMemo(() => {
        const ranges = {};
        Object.entries(visibleColumns).forEach(([key, visible]) => {
            if (!visible || key === 'displayName' || key === 'Records') return;
            const values = filteredPlayerData
                .map(p => p[key])
                .filter(v => v !== null && v !== undefined && !isNaN(v));
            if (values.length > 0) {
                ranges[key] = { min: Math.min(...values), max: Math.max(...values) };
            } else {
                ranges[key] = { min: 0, max: 0 };
            }
        });
        return ranges;
    }, [filteredPlayerData, visibleColumns]);

    const getMaxValue = useCallback((property) => statsRanges[property]?.max ?? 0, [statsRanges]);
    const getMinValue = useCallback((property) => statsRanges[property]?.min ?? 0, [statsRanges]);

    const sorter = useMemo(() => compareValues(sortedColumn, sortDirection), [sortedColumn, sortDirection]);
    const sortedPlayerAverages = useMemo(() =>
        [...filteredPlayerData].sort(sorter),
        [filteredPlayerData, sorter]
    );

    const selectedSet = useMemo(() => new Set(selectedPlayers), [selectedPlayers]);
    const displayData = useMemo(() =>
        ShowSelected
            ? sortedPlayerAverages.filter(p => selectedSet.has(p.displayName))
            : sortedPlayerAverages,
        [sortedPlayerAverages, ShowSelected, selectedSet]
    );


    const handleLeagueChange = (selectedOptions) => {
        const leagueValues = selectedOptions.map(option => option.value);
        setFilterLeague(leagueValues)
        setSelectedLeague(selectedOptions);
        setPlayerData([]);

    };

    const handlePatchChange = (selectedOptions) => {
        const leagueValues = selectedOptions.map((option) => option.value);
        setFilterPatch(leagueValues);
        setSelectedPatch(selectedOptions);
        setPlayerData([]);

    };

    const handleTeamChange = (selectedOptions) => {
        const teamValues = selectedOptions.map(option => option.value);
        setFilterTeam(teamValues);
        setSelectedTeam(selectedOptions);
    };

    const handlePlayerCheckboxChange = (displayName) => {
        if (selectedPlayers.includes(displayName)) {
            setSelectedPlayers(selectedPlayers.filter((name) => name !== displayName));
        } else {
            setSelectedPlayers([...selectedPlayers, displayName]);
        }
    };


    const renderSortArrow = (column) => {
        if (sortedColumn !== column) return null;
        return sortDirection === 'asc' ? "▲" : "▼";
    };


    function formatSeconds(seconds) {
        if (seconds == null || isNaN(seconds)) return "-";

        const totalSeconds = Math.floor(seconds);
        const minutes = Math.floor(totalSeconds / 60);
        const remainingSeconds = totalSeconds % 60;

        return `${minutes.toString().padStart(2, "0")}:${remainingSeconds
            .toString()
            .padStart(2, "0")}`;
    }



    const visibleColumnsList = useMemo(
        () => Object.entries(visibleColumns).filter(([, v]) => v),
        [visibleColumns]
    );

    const playerColors = useMemo(() => {
        const colors = {};
        filteredPlayerData.forEach(player => {
            colors[player.displayName] = {};
            visibleColumnsList.forEach(([key]) => {
                colors[player.displayName][key] = getColorForCell(key, player[key], player);
            });
        });
        return colors;
    }, [filteredPlayerData, visibleColumnsList, statsRanges]);

    const maxdisplayNameLength = useMemo(() => {
        if (!displayData || displayData.length === 0) return 0;

        return Math.max(...displayData.map(p =>
            p.displayName ? p.displayName.length : 0
        ));
    }, [displayData]);

    const calculateColumnWidths = useCallback(() => {
        if (!columnNames) return [];

        const columnWidths = [];

        columnWidths.push('51px');

        visibleColumnsList.forEach(([key], index) => {
            if (key === 'displayName') {
                const width = Math.max(150, maxdisplayNameLength * 12 + 30);
                columnWidths.push(`${width}px`);
            } else {
                const nameLength = columnNames[key]?.length || 0;
                const width = Math.max(80, nameLength * 12 + 30);
                columnWidths.push(`${width}px`);
            }
        });

        return columnWidths;
    }, [visibleColumnsList, columnNames, maxdisplayNameLength]);

    const MemoizedHeader = React.memo(() => {
        const columnWidths = calculateColumnWidths();
        const [tooltip, setTooltip] = useState({ visible: false, text: "", x: 0, y: 0 });

        const showTooltip = (e, text) => {
            const rect = e.currentTarget.getBoundingClientRect();
            setTooltip({
                visible: true,
                text,
                x: rect.x + rect.width / 2,
                y: rect.y - 10,
            });
        };

        const hideTooltip = () => {
            setTooltip({ visible: false, text: "", x: 0, y: 0 });
        };

        return (
            <>
                <tr>
                    {columnWidths.map((width, index) => {
                        if (index === 0)
                            return (
                                <th
                                    key="checkbox"
                                    style={{
                                        width,
                                        left: "-1px",
                                        background: "linear-gradient(180deg, #1a1a1a, #2a2a2a, #1a1a1a)",
                                    }}
                                ></th>
                            );

                        const [key] = visibleColumnsList[index - 1];
                        return (
                            <th
                                key={key}
                                style={{
                                    width,
                                    whiteSpace: "nowrap",
                                    background: backgroundColor[key],
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    textAlign: key === "displayName" ? "left" : "center",
                                    cursor: "help",
                                    userSelect: "none",
                                }}
                                onMouseEnter={(e) => showTooltip(e, columnNamesDescription[key])}
                                onMouseLeave={hideTooltip}
                                onClick={() => handleSort(key)}
                            >
                                {columnNames[key]}
                                {renderSortArrow(key)}
                            </th>
                        );
                    })}
                </tr>

                {/* Tooltip */}
                <AnimatePresence>
                    {tooltip.visible && (
                        <motion.div
                            style={{
                                position: "fixed",
                                zIndex: 1000,
                                padding: "4px 8px",
                                fontSize: "12px",
                                color: "#fff",
                                backgroundColor: "#333",
                                borderRadius: "4px",
                                boxShadow: "0 2px 6px rgba(0,0,0,0.3)",
                                pointerEvents: "none",
                                top: tooltip.y,
                                left: tooltip.x,
                                transform: "translate(-50%, -100%)",
                                maxWidth: "240px",
                                textAlign: "center",
                                whiteSpace: "normal",
                            }}
                            initial={{ opacity: 0, y: 5 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: 5 }}
                            transition={{ duration: 0.15 }}
                        >
                            {tooltip.text}
                        </motion.div>
                    )}
                </AnimatePresence>
            </>
        );
    });

    const MemoizedRow = React.memo(({ player, style }) => {
        const columnWidths = calculateColumnWidths();
        const cells = useMemo(() => {
            return columnWidths.map((width, index) => {
                if (index === 0) {
                    return (
                        <td key="checkbox" style={{ width, textAlign: 'center' }}>
                            <input
                                type="checkbox"
                                checked={selectedSet.has(player.displayName)}
                                onChange={() => memoizedCheckboxChange(player.displayName)}
                            />
                        </td>
                    );
                }

                const [key] = visibleColumnsList[index - 1];
                const value = player[key];
                const bgColor = playerColors[player.displayName]?.[key] || 'white';

                if (key === 'displayName') {
                    const nameWithoutTag = getPlayerNameWithoutTeamTag(value) || value;
                    const avatarUrl = PlayerPhoto?.[nameWithoutTag] || PlayerPhoto?.[1];

                    return (
                        <td
                            key={key}
                            style={{
                                width,
                                backgroundColor: bgColor,
                                whiteSpace: "nowrap",
                                padding: "4px 8px",
                            }}
                        >
                            <div
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "6px",
                                }}
                            >
                                <img
                                    src={avatarUrl}
                                    alt={`${nameWithoutTag}'s avatar`}
                                    style={{
                                        width: "24px",
                                        height: "24px",
                                        borderRadius: "50%",
                                    }}
                                />

                                <span
                                    onClick={() => handlePlayerClick(player.displayName)}
                                    style={{ cursor: "pointer" }}
                                >
                                    {player.displayName}
                                </span>
                            </div>
                        </td>
                    );
                }

                return (
                    <td
                        key={key}
                        style={{
                            width,
                            backgroundColor: bgColor,
                            whiteSpace: "nowrap",
                            textAlign: key === "displayName" ? "left" : "center",
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                        }}
                    >
                        {formatValue(value, key)}
                    </td>
                );
            });
        }, [player, playerColors, columnWidths]);

        return <tr style={style}>{cells}</tr>;
    });

    const VirtualizedTable = () => {
        const rowVirtualizer = useVirtualizer({
            count: displayData.length,
            getScrollElement: () => tableContainerRef.current,
            estimateSize: () => 40,
            overscan: 20,
        });

        return (
            <div
                ref={tableContainerRef}

                className='team-performance-table-container'
                style={{
                    height: '80vh',
                    overflow: 'auto',
                    marginLeft: "10px",
                    marginRight: "10px"
                }}
            >
                <table className='fancy-table-players' style={{ overflow: "visible", tableLayout: 'fixed', display: 'table', width: 'fit-content', backgroundColor: 'rgb(30, 30, 30)' }}>
                    <thead style={{ position: 'sticky', top: "-1px", zIndex: 2 }}>
                        <MemoizedHeader />
                    </thead>
                    <tbody style={{ height: `${rowVirtualizer.getTotalSize()}px`, position: 'relative' }}>
                        {rowVirtualizer.getVirtualItems().map(virtualRow => {
                            const player = displayData[virtualRow.index];
                            return (
                                <MemoizedRow
                                    key={virtualRow.key}
                                    player={player}
                                    style={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        width: '100%',
                                        height: `${virtualRow.size}px`,
                                        transform: `translateY(${virtualRow.start}px)`,
                                    }}
                                />
                            );
                        })}
                    </tbody>
                </table>
            </div>
        );
    };

    const handleColumnToggle = useCallback((column) => {
        setVisibleColumns(prev => ({
            ...prev,
            [column]: !prev[column]
        }));
    }, []);

    useEffect(() => {
        if (tableContainerRef.current) {
            if (scrollTopRef.current > 0) {
                tableContainerRef.current.scrollTop = scrollTopRef.current;
                scrollTopRef.current = 0;
            }
            if (scrollLeftRef.current > 0) {
                tableContainerRef.current.scrollLeft = scrollLeftRef.current;
                scrollLeftRef.current = 0;
            }
        }
    }, [sortedPlayerAverages]);

    const memoizedCheckboxChange = useCallback(handlePlayerCheckboxChange, [selectedPlayers]);




    return (
        <div style={{ display: "flex", flexDirection: "column", color: 'white' }}>
            < div style={{
                width: "100%", padding: "10px", paddingBottom: "0px", position: 'sticky', top: "-1px", zIndex: "10", backdropFilter: "blur(8px)", display: "flex", justifyContent: 'center'
            }}>
                <div className='filters'>
                    <div className='short-chooser'>
                        <LeagueCustomSelect options={leagueOption} selectedOption={selectedLeague} onOptionChange={handleLeagueChange} />
                    </div>
                    <div className='short-chooser'>
                        <PatchCustomSelect options={patchOption} selectedOption={selectedPatch} onOptionChange={handlePatchChange} />
                    </div>
                    <div className='short-chooser'>
                        <TeamCustomSelect options={teamOptions} selectedOption={selectedTeam} onOptionChange={handleTeamChange} />
                    </div>

                    <button
                        className="button-dark"
                        onClick={() => setShowSelected(!ShowSelected)}
                        style={{
                            backgroundColor: ShowSelected ? "#1e88e5" : "#2c2c2c",
                            color: "#f1f1f1",
                            border: "1px solid #444",
                            margin: '10px',
                            borderRadius: "5px",
                            cursor: "pointer",
                            transition: "background-color 0.2s ease-in-out",
                        }}
                        onMouseEnter={(e) => (e.target.style.backgroundColor = ShowSelected ? "#1565c0" : "#3a3a3a")}
                        onMouseLeave={(e) => (e.target.style.backgroundColor = ShowSelected ? "#1e88e5" : "#2c2c2c")}
                    >
                        Only Selected
                    </button>

                    <div style={{ position: 'relative' }}>
                        <button style={{ margin: '10px' }} className="button-dark" onClick={toggleDropdown}>{" Toggle Columns "}</button>
                        {isOpen && (
                            <div style={{ position: 'absolute', top: '100%', height: "300px", overflowY: "scroll", left: 0, backgroundColor: '#2c2c2c', boxShadow: '0px 8px 16px rgba(0,0,0,0.2)', zIndex: 1000 }}>
                                {Object.entries(visibleColumns).map(([key, visible]) => (
                                    <label key={key} style={{ display: 'block', color: "white", whiteSpace: 'nowrap' }}>
                                        <input
                                            type="checkbox"
                                            checked={visible}
                                            onChange={() => handleColumnToggle(key)}

                                        /> {columnNames[key]}
                                    </label>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div >

            <div style={{ backgroundColor: 'black' }}>
                <div className='team-performance-table-container' style={{ height: '100vh' }}>
                    <VirtualizedTable />
                </div>
            </div>

            <div>
                <PlayersScatterPlot
                    data={displayData}
                    selectedPlayers={selectedPlayers}
                    columnOptions={visibleColumns}
                />
            </div>

        </div >
    );
};

export default Players2;

